import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { ToasterService, Toast, ToasterConfig } from 'angular2-toaster';
import { CookieService } from "angular2-cookie/core";
import { Subscription, Observable } from "rxjs";

import { Adjustment } from '../model/adjustment';
import { Quote } from '../model/quote';
import { Constants } from "../model/constants";
import { CurrentUser } from "../model/current-user";

import { ApplicationStateService } from '../services/application-state.service';
import { AdjustmentService } from '../services/adjustments.service';
import { LoadingSpinnerService } from "../services/loading-spinner.service";
import { PageTitleService } from "../services/page-title.service";
import { QuoteInfoService } from "../services/quote-info.service";
import { CurrentUserService } from "../services/current-user.service";
import { QuoteSummaryService } from "../services/quote-summary.service";
import { ModalDialogService } from '../reusable-components/modal-dialog/modal-dialog.service';
import { BrowserWidthService } from "../services/browser-width.service";
import { HttpResponseService } from '../services/httpresponse-service';

@Component({
    selector: 'my-set-markup',
    template: require('./set-markup.component.html'),
    styles: [require('./set-markup.component.scss')]
})
export class SetMarkupComponent implements OnInit, OnDestroy {
    Title: string = 'Set Markups';
    currentAdjustment: Adjustment;

    config: ToasterConfig = new ToasterConfig({
        positionClass: this.browserWidthService.getDisplayMode().width.innerWidth < 769 ? 'toast-bottom-full-width' : 'toast-bottom-right', // positionClass: 'toast-bottom-right'
    });
    quote: Quote;

    baseMarkupAmount: number;
    baseMarkupAmountString: string;
    afterBaseMarkup: number;
    afterOptionsMarkup: number;
    percentRange: number;
    optionsPercent: number;
    StartingHomePrice: number = 50000;
    StartingOptionsPrice: number = 10000;
    quoteId: number;
    baseCost: number = 0;

    modifiedBy: string;
    stateError: string = '';
    flatPriceError: string = '';
    mchBaseUrl: string;
    baseMarkupPercent: string;
    optionsMarkupPercent: string;

    percentView: boolean = true;
    flatView: boolean = false;
    validMarkup: boolean = true;
    validOption: boolean = true;
    validFlat: boolean = true;
    stateIsInvalid: boolean = false;
    disableMarkups: boolean = false;
    showingDeactivateModal: boolean = false;
    subscription: Subscription = new Subscription();
    currentUser: CurrentUser;
    height: number;

    constructor(
        private applicationStateService: ApplicationStateService,
        private adjustmentService: AdjustmentService,
        private router: Router,
        private route: ActivatedRoute,
        private toasterService: ToasterService,
        private spinner: LoadingSpinnerService,
        private pageTitleService: PageTitleService,
        private quoteInfoService: QuoteInfoService,
        private currentUserSerice: CurrentUserService,
        private quoteSummaryService: QuoteSummaryService,
        private modalDialogService: ModalDialogService,
        private cookieService: CookieService,
        public browserWidthService: BrowserWidthService,
        private httpResponseService: HttpResponseService
    ) {

        const routeParamsSub = this.route.params.subscribe(x => {
            this.quoteId = x['quoteId'];
            this.quoteInfoService.quoteId$.next(this.quoteId);
        });
        this.subscription.add(routeParamsSub);

        const currentUserSub = this.currentUserSerice.currentUser$.subscribe(x => {
            this.currentUser = x;
            this.modifiedBy = x.cmhUserId + '-' + x.firstName + ' ' + x.lastName;
            this.disableMarkups = false;
            if (!x.isPlantUser) {
                this.disableMarkups = x.roles.includes('Rtl_Admin') ? false : true;
            }
        });
        this.subscription.add(currentUserSub);
        this.mchBaseUrl = this.cookieService.get('mchBaseUrl');

        const resizeEventSub = Observable.fromEvent(window, 'resize')
            .subscribe(() => {
                if (document.getElementById('content-wrapper') !== undefined && document.getElementById('content-wrapper') !== null) {
                    this.height = document.getElementById('content-wrapper').scrollHeight + 114;
                }
            });
        this.subscription.add(resizeEventSub);
    }

    ngOnInit() {
        this.pageTitleService.pageTitle.next({ title: Constants.CmhConnectionTitle });

        this.currentAdjustment = new Adjustment();
        this.currentAdjustment.quoteId = this.quoteId;
        this.spinner.showSpinner();

        if (this.quoteId === undefined || this.quoteId === 0) {
            this.spinner.hideSpinner();
            this.stateIsInvalid = true;
            this.modalDialogService.showMessage({
                headerText: 'Set Markups',
                message: Constants.NewQuoteMessage
            }).subscribe(messageReply => {
                this.redirectRequired(true, false, false);
            });
        } else {
            const refreshSub = this.applicationStateService.refreshStateFromDB(this.quoteId, true)
                .subscribe(
                    (dbQuote => {
                        if (dbQuote !== undefined && dbQuote !== null) {
                            this.pageTitleService.pageTitle.next({
                                title: Constants.CmhConnectionTitle,
                                subTitle: Constants.CurrentQuoteSubTitle + (
                                    dbQuote.iseriesQuoteNumber > 0 && dbQuote.QuoteStatusesID > 0
                                        ? dbQuote.iseriesQuoteNumber
                                        : this.quoteId
                                )
                            });
                            this.quote = dbQuote;
                            this.quoteInfoService.quote$.next(dbQuote);
                            const canAccessSub = this.quoteInfoService.canAccessQuote$.subscribe(canAccessQuote => {
                                if (canAccessQuote === true) {
                                    if (this.quote.productLineId === undefined || this.quote.productLineId === null || this.quote.productLineId === 0) {
                                        this.spinner.hideSpinner();
                                        this.stateIsInvalid = true;
                                        const modalSub = this.modalDialogService.showMessage({
                                            headerText: Constants.NoProductLineHeader,
                                            message: Constants.NoProductLineMessage
                                        }).subscribe(messageReply => {
                                            this.redirectRequired(false, true, false);
                                        });
                                        this.subscription.add(modalSub);
                                    } else {
                                        if (this.quote.modelId === undefined || this.quote.modelId === null || this.quote.modelId === 0) {
                                            this.spinner.hideSpinner();
                                            this.stateIsInvalid = true;
                                            const modalSub = this.modalDialogService.showMessage({
                                                headerText: Constants.NoModelHeader,
                                                message: Constants.NoModelMessage
                                            }).subscribe(messageReply => {
                                                this.redirectRequired(false, false, true);
                                            });
                                            this.subscription.add(modalSub);
                                        } else {
                                            const pricingHeaderSub = this.quoteSummaryService.getPricingHeader(dbQuote.quoteId)
                                                .subscribe(
                                                    (x => {
                                                        this.baseCost = x.BaseCost;
                                                        if (x.AdjustmentInfo !== undefined && x.AdjustmentInfo !== null) {
                                                            this.currentAdjustment = x.AdjustmentInfo;
                                                        } else {
                                                            this.currentAdjustment = new Adjustment();
                                                            this.currentAdjustment.quoteId = this.quoteId;
                                                        }
                                                        this.setupForm();
                                                    }),
                                                    (error => { this.spinner.hideSpinner(); this.httpResponseService.httpErrorResponse$.next(error); }),
                                                    (() => { this.spinner.hideSpinner(); })
                                                );
                                            this.subscription.add(pricingHeaderSub);
                                        }
                                    }
                                } else {
                                    this.spinner.hideSpinner();
                                }
                            });
                            this.subscription.add(canAccessSub);
                        } else {
                            this.spinner.hideSpinner();
                            this.stateIsInvalid = true;
                            const modalDisplaySub = this.modalDialogService.showMessage({
                                headerText: 'Set Markups',
                                message: Constants.QuoteNotFoundMessage.replace('#quoteid#', this.quoteId.toString())
                            }).subscribe(messageReply => {
                                this.redirectRequired(true, false, false);
                            });
                            this.subscription.add(modalDisplaySub);
                        }
                    }),
                    (error => { this.spinner.hideSpinner(); this.httpResponseService.httpErrorResponse$.next(error); })
                );
            this.subscription.add(refreshSub);
        }
    }

    ngOnDestroy() {
        this.saveHomePricingInfoOnDestroy();
        this.subscription.unsubscribe();
    }

    canDeactivate(): boolean {
        if (this.quoteInfoService.canAccessQuote$.value === true && !this.stateIsInvalid) {
            this.calculateAfterBaseMarkup();
            this.calculateAfterOptionsMarkup();
            if ((this.validFlat && this.validOption && this.validMarkup) || this.disableMarkups) {
                return true;
            } else {
                if (!this.showingDeactivateModal) {
                    this.showingDeactivateModal = true;
                    this.modalDialogService.showMessage({
                        headerText: 'Set Markups',
                        message: Constants.ValidationErrorsMessage
                    }).subscribe(messageReply => {
                        this.showingDeactivateModal = false;
                    });
                }
                return false;
            }
        } else {
            return true;
        }
    }

    saveHomePricingInfo() {
        this.calculateAfterBaseMarkup();
        this.calculateAfterOptionsMarkup();

        let quote = this.applicationStateService.getQuote();
        this.currentAdjustment.modifiedBy = this.modifiedBy;
		this.currentAdjustment.sequenceNumber = 1;
		this.currentAdjustment.quoteRevisionNumber = quote.revisionNumber;

        if (this.validFlat && this.validMarkup && this.validOption) {
            if (this.currentAdjustment.adjustmentId > 0) {
                this.spinner.showSpinner();
                const updateAdjustmentSub = this.adjustmentService.updateAdjustment(this.currentAdjustment)
                    .map(savedAdjustment => {
                        this.currentAdjustment.adjustmentId = savedAdjustment.adjustmentId;
                        this.toasterService.pop('success', 'Success!', 'Markup was updated');
                    })
                    .subscribe(
                        (data => { }),
                        (error => { this.spinner.hideSpinner(); this.httpResponseService.httpErrorResponse$.next(error); }),
                        (() => {
                            this.spinner.hideSpinner();
                            this.router.navigateByUrl(`/order-information/${this.quote.quoteId}`);
                        })
                    );
                this.subscription.add(updateAdjustmentSub);
            } else {
                this.spinner.showSpinner();
                const insertAdjustmentSub = this.adjustmentService.insertAdjustment(this.currentAdjustment)
                    .map(savedAdjustment => {
                        this.currentAdjustment.adjustmentId = savedAdjustment.adjustmentId;
                        this.toasterService.pop('success', 'Success!', 'New Markup saved');
                    })
                    .subscribe(
                        (data => { }),
                        (error => { this.spinner.hideSpinner(); this.httpResponseService.httpErrorResponse$.next(error); }),
                        (() => {
                            this.spinner.hideSpinner();
                            this.router.navigateByUrl(`/order-information/${this.quote.quoteId}`);
                        })
                    );
                this.subscription.add(insertAdjustmentSub);
            }
        }
    }

    saveHomePricingInfoOnDestroy() {
        this.calculateAfterBaseMarkup();
        this.calculateAfterOptionsMarkup();

        let quote = this.applicationStateService.getQuote();
        this.currentAdjustment.modifiedBy = this.modifiedBy;
		this.currentAdjustment.sequenceNumber = 1;
		this.currentAdjustment.quoteRevisionNumber = quote.revisionNumber;

        if (this.validFlat && this.validMarkup && this.validOption) {
            if (this.currentAdjustment.adjustmentId > 0) {
                const updateAdjustmentSub = this.adjustmentService.updateAdjustment(this.currentAdjustment)
                    .map(savedAdjustment => {
                        this.currentAdjustment.adjustmentId = savedAdjustment.adjustmentId;
                        this.quoteInfoService.adjustment$.next(savedAdjustment);
                    })
                    .subscribe(
                        (data => { }),
                        (error => { this.spinner.hideSpinner(); this.httpResponseService.httpErrorResponse$.next(error); }),
                        (() => { this.quoteSummaryService.calculatePrices(); })
                    );
                this.subscription.add(updateAdjustmentSub);
            } else {
                const insertAdjustmentSub = this.adjustmentService.insertAdjustment(this.currentAdjustment)
                    .map(savedAdjustment => {
                        this.currentAdjustment.adjustmentId = savedAdjustment.adjustmentId;
                        this.quoteInfoService.adjustment$.next(savedAdjustment);
                    })
                    .subscribe(
                        (data => { }),
                        (error => { this.spinner.hideSpinner(); this.httpResponseService.httpErrorResponse$.next(error); }),
                        (() => { this.quoteSummaryService.calculatePrices(); })
                    );
                this.subscription.add(insertAdjustmentSub);
            }
        }
    }

    reset() {
        this.afterBaseMarkup = this.StartingHomePrice;
        this.afterOptionsMarkup = this.StartingOptionsPrice;
        this.optionsMarkupPercent = '0%';
        this.optionsPercent = 0;

        this.percentRange = 70;
        this.baseMarkupPercent = '70%';

        this.baseMarkupAmount = 0;
        this.baseMarkupAmountString = '$0';

        this.validMarkup = true;
        this.validOption = true;
        this.validFlat = true;
    }

    setPercentView() {
        this.percentView = true;
        this.flatView = false;
        this.validFlat = true;
        this.percentRange = 70;
        this.baseMarkupPercent = '70%';
        this.baseMarkupAmount = 0;
        this.baseMarkupAmountString = '$0';

    }

    setFlatView() {
        this.percentView = false;
        this.flatView = true;
        this.percentRange = 70;
        this.baseMarkupPercent = '70%';

        this.baseMarkupAmount = 0;
        this.baseMarkupAmountString = '$0';

    }

    calculateAfterBaseMarkup() {
        let maxBaseCost = ((this.baseCost * 1.70) - (this.baseCost));

        if (this.percentView) {
            // percentage view
            this.percentRange = this.percentRange === undefined || this.percentRange.toString() === '' ? 0 : this.percentRange;
            if (this.percentRange > 70) { this.percentRange = 70.00; }
            this.baseMarkupPercent = this.percentRange.toString().replace('%', '');

            this.currentAdjustment.baseAdjustmentFlat = 0;
            this.baseMarkupAmountString = '$0';
            if ((isNaN(Number(this.baseMarkupPercent))) || (this.percentRange < 0)) {
                this.validMarkup = false;
            } else {
                this.validMarkup = true;
            }

            this.currentAdjustment.baseAdjustmentPercent = (parseFloat(this.percentRange.toString()));
            this.afterBaseMarkup = parseFloat((this.StartingHomePrice * (1 + (this.currentAdjustment.baseAdjustmentPercent / 100))).toFixed(2));
            this.baseMarkupPercent = this.formatPercent(this.percentRange.toString().replace('%', ''));

        } else {
            // base markup view
            this.validMarkup = true;

            if (this.baseMarkupAmountString !== undefined && this.baseMarkupAmountString.length > 0 && this.baseMarkupAmountString !== '$') {
                this.baseMarkupAmount = parseFloat(this.baseMarkupAmountString.replace('$', ''));
            }

            this.baseMarkupPercent = '0%';
            this.currentAdjustment.baseAdjustmentPercent = 0;

            if ((this.baseMarkupAmount <= maxBaseCost) && (this.baseMarkupAmount >= 0)) {
                this.validFlat = true;

                this.currentAdjustment.baseAdjustmentFlat = this.baseMarkupAmount;
                this.currentAdjustment.baseAdjustmentPercent = 0;
                this.afterBaseMarkup = parseFloat((this.StartingHomePrice + this.currentAdjustment.baseAdjustmentFlat).toFixed(2));

            } else {
                this.validFlat = false;
                this.flatPriceError = 'Base Markup Price must be between 0 and ' + this.formatCurrency(maxBaseCost.toString()) + ', and must be a number.'
            }

            if (this.baseMarkupAmount !== undefined) {
                this.baseMarkupAmountString = this.formatCurrency(this.baseMarkupAmount.toString());
            }
        }

    }

    calculateAfterOptionsMarkup() {
        if (this.optionsPercent === undefined || this.optionsPercent.toString() === '') {
            this.optionsPercent = 0;
        }
        if (Number(this.optionsPercent) > 500) { this.optionsPercent = 500; }
        this.optionsMarkupPercent = this.optionsPercent.toString().replace('%', '');

        if ((isNaN(Number(this.optionsMarkupPercent))) || (this.optionsPercent < 0)) {
            this.validOption = false;
        } else {
            this.validOption = true;

            this.currentAdjustment.optionsAdjustmentPercent = (parseFloat(this.optionsPercent.toString().replace('%', '')));
            this.afterOptionsMarkup = parseFloat((this.StartingOptionsPrice * (1 + (this.currentAdjustment.optionsAdjustmentPercent / 100))).toFixed(2));
        }

        this.optionsMarkupPercent = this.formatPercent(this.currentAdjustment.optionsAdjustmentPercent.toString());

    }

    selectValue(event: MouseEvent, property: string) {
        event.currentTarget.addEventListener('click', () => {
            this[property] = '';
        });
    }

    setupForm() {
        this.spinner.hideSpinner();
        if (this.currentAdjustment !== undefined) {
            this.currentAdjustment.baseAdjustmentPercent = this.currentAdjustment.baseAdjustmentPercent === undefined ? 0 : this.currentAdjustment.baseAdjustmentPercent;
            this.currentAdjustment.baseAdjustmentFlat = this.currentAdjustment.baseAdjustmentFlat === undefined ? 0 : this.currentAdjustment.baseAdjustmentFlat;
            this.currentAdjustment.optionsAdjustmentPercent = this.currentAdjustment.optionsAdjustmentPercent === undefined ? 0 : this.currentAdjustment.optionsAdjustmentPercent;

            this.percentRange = this.currentAdjustment.baseAdjustmentPercent;
            this.currentAdjustment.baseAdjustmentFlat = this.currentAdjustment.baseAdjustmentFlat;
            this.optionsPercent = this.currentAdjustment.optionsAdjustmentPercent;

            if (this.currentAdjustment.baseAdjustmentPercent !== 0 || (this.currentAdjustment.baseAdjustmentPercent === 0 && this.currentAdjustment.baseAdjustmentFlat === 0)) {
                this.baseMarkupPercent = this.formatPercent(this.currentAdjustment.baseAdjustmentPercent);
                this.baseMarkupAmountString = '$0';
                this.afterBaseMarkup = parseFloat((this.StartingHomePrice * (1 + (this.currentAdjustment.baseAdjustmentPercent / 100))).toFixed(2));
                this.percentView = true;
                this.flatView = false;
            } else {
                if (this.currentAdjustment.baseAdjustmentFlat !== 0) {
                    this.baseMarkupAmountString = this.formatCurrency(this.currentAdjustment.baseAdjustmentFlat.toString());
                    this.baseMarkupPercent = '0%';
                    this.afterBaseMarkup = parseFloat((this.StartingHomePrice + this.currentAdjustment.baseAdjustmentFlat).toFixed(2));
                    this.percentView = false;
                    this.flatView = true;
                } else {
                    this.afterBaseMarkup = this.StartingHomePrice;
                    this.setPercentView();
                    this.optionsMarkupPercent = '0%';
                }
            }

            if (this.currentAdjustment.optionsAdjustmentPercent != 0) {
                this.optionsMarkupPercent = this.formatPercent(this.currentAdjustment.optionsAdjustmentPercent)
                this.afterOptionsMarkup = parseFloat((this.StartingOptionsPrice * (1 + (this.currentAdjustment.optionsAdjustmentPercent / 100))).toFixed(2));
            } else {
                this.optionsMarkupPercent = '0%';
                this.afterOptionsMarkup = this.StartingOptionsPrice;
            }
        } else {
            this.setPercentView();
        }
    }

    redirectRequired(newQuotesRedirect: boolean, productLinesRedirect: boolean, modelsRedirect: boolean) {
        this.stateIsInvalid = false;
        if (newQuotesRedirect) {
            this.router.navigate([Constants.NewQuotesUrl]);
        }
        if (productLinesRedirect) {
            window.location.href = `${this.mchBaseUrl}/${([Constants.ProductLineUrl.replace('#quoteid#', this.quoteId.toString())])}`;
        }
        if (modelsRedirect) {
            window.location.href = `${this.mchBaseUrl}/${([Constants.ModelUrl.replace('#quoteid#', this.quoteId.toString())])}`;
        }
    }

    formatPercent(basePercent: any) {
        let returnValue = '';

        if (isNaN(Number(basePercent))) {
            return basePercent;
        } else {
            let percentString = basePercent.toString();
            if (percentString.indexOf('.') !== -1) {
                if (parseFloat(percentString) - parseFloat(parseFloat(percentString).toFixed(0)) === 0) {
                    returnValue = (parseFloat(percentString).toFixed(0));
                } else {
                    returnValue = (parseFloat(percentString).toFixed(2));
                }
            } else {
                returnValue = (parseFloat(percentString).toString());
            }
            returnValue = returnValue + '%';
            return returnValue
        }


    }

    formatCurrency(baseCurrency: any) {
        let returnValue = '';

        if (isNaN(Number(baseCurrency))) {
            return baseCurrency;
        } else {
            let currencyString = baseCurrency.toString();

            if (currencyString.indexOf('.') !== -1) {
                if (parseFloat(currencyString) - parseFloat(parseFloat(currencyString).toFixed(0)) === 0) {
                    returnValue = (parseFloat(currencyString).toFixed(0));
                } else {
                    returnValue = (parseFloat(currencyString).toFixed(2));
                }
            } else {
                returnValue = (parseFloat(currencyString).toString());
            }
            returnValue = '$' + returnValue;
            return returnValue
        }
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./src/app/set-markup/set-markup.component.ts